import { Box, Stack, Typography } from '@mui/material'
import { StartHome } from '../../../assets/svgs/StartHome'
import { useEffect, useState } from 'react'
import { AnnualPlanningTeamModal } from '../planning/AnnualPlanningTeamModal'
import { type AnnualPlanRole } from '../../../models/AnnualPlanRole'
import { StartImplementationPlan } from '../../../services/AnnualPlanService'
import { AnnualPlanStatus } from '../../../models/AnnualPlanStatus'
import { useAuth } from '../../../hooks/use-auth'
import { SecurityLevel } from '../../../utils/genericTypes'

interface Props {
  annualPlanId: number
  rcnId: number
  userRole: AnnualPlanRole | null
  annualPlanStatus: AnnualPlanStatus
  onImplementationStarted: () => void
}
export const ImplementationGetStarted = (props: Props): JSX.Element => {
  const [showPlanTeamModal, setShowPlanTeamModal] = useState(false)
  const auth = useAuth()
  useEffect(() => {
    const implementationPhaseNotStarted = props.annualPlanStatus === AnnualPlanStatus.Finalized
    if (implementationPhaseNotStarted) {
      if (auth.user?.securityLevel === SecurityLevel.RCNAdmin) {
        setShowPlanTeamModal(true)
      }
    }
  }, [props.userRole])

  const onCloseModal = (): void => {
    setShowPlanTeamModal(false)
  }

  const onPlanTeamSubmit = async (): Promise<void> => {
    await StartImplementationPlan(props.annualPlanId, props.rcnId)
    setShowPlanTeamModal(false)
    props.onImplementationStarted()
  }

  return <Box data-testid='implementationGetStarted'>
    <StartHome
      style={{
        position: 'relative',
        width: '100%',
        height: 'fit-content',
        borderRadius: '12px',
        display: 'flex',
        flexDirection: 'column',
        gap: '32px',
        marginBottom: '48px',
        marginTop: '20px'
      }}
    />

    <Stack spacing='1em'>
      <Typography variant='h3' mb='24px'>Implementation Kickoff</Typography>
      <Typography variant='body1'>
        Now that you have completed your Annual Plan and have an RCN Contract in place, the next step is to complete your Implementation Plan. The Implementation Plan has two phases: “Updates” and “Tracking.” You will complete “Updates” to provide details about the activities you have planned in the Contract. “Tracking” allows you to enter progress data once activities have been completed.
      </Typography>

      <Box>
        <Typography variant='h5'>Updates Phase</Typography>
        <Typography variant='body1'>
          The Implementation Plan provides the information from the Contract so it can be updated as needed.
        </Typography>
      </Box>

      <Box>
        <Typography variant='h5'>During the Updates Phase:</Typography>
        <ul>
          <li>Use the status dropdowns to communicate your progress and lock or unlock pages for editing.</li>
          <li>Add, edit, or delete any activities in the Implementation Plan as needed.</li>
          <li>Alerts will inform you of any budget changes requiring START review that result from your updates.</li>
        </ul>
      </Box>

      <Box>
        <Typography variant='h5'>Updates are required for all RCN Operations and Priority Activities</Typography>
      </Box>

      <Typography variant='body1'>
        When finished, ensure all statuses are set to “Updates Submitted” in order to share the Implementation Plan Updates with START for review.
      </Typography>

      <Box>
        <Typography variant='h5'>Tracking Phase Process</Typography>
        <Typography variant='body1'>
          After your Implementation Plan has gone through START Review, the Tracking Phase begins.
        </Typography>
      </Box>

      <Box>
        <Typography variant='h5'>During the Tracking Phase:</Typography>
        <ul>
          <li>Use the status dropdowns to indicate your progress and lock or unlock pages for editing.</li>
          <li>Click the “Tracking Icon” next to a planned activity in order to report the data and/or progress associated with that activity. Please note - you can continue to edit the Tracking Data until you submit your final Implementation Plan to START at the end of the year.</li>
          <li>
            Document progress in the Data Tools Tab for:
            <ul>
              <li>Coaching Capacity Tool</li>
              <li>Peer to Peer Program Data</li>
              <li>Focus Student Data</li>
            </ul>
          </li>
          <li>Complete tracking for Mid-Year and submit the Mid-Year Progress Report.</li>
          <li>Continue tracking and documenting your progress on the data tools after mid-year, through the end of the year.</li>
        </ul>
      </Box>

      <Typography variant='body1'>
        Before final submission, set all statuses to “Tracking Completed.”
      </Typography>
    </Stack>

    {showPlanTeamModal &&
      <AnnualPlanningTeamModal
        closeModal={onCloseModal}
        onSubmit={onPlanTeamSubmit}
        annualPlanId={props.annualPlanId}
        isImplementationPlan={true}
      />
    }
  </Box>
}
